import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../components/DataTable'
import { adminFinance } from '../api/finance'
import type { ProblemItem } from '../api/financeTypes'
import { DASH, decimalSortKey, formatCount, formatMoney } from '../lib/money'
import { ProblemActions } from './actions'
import { userPage } from './links'
import { useFinance } from './financeContext'
import { COVER_FLAG, KIND_LABEL, KIND_ORDER, SEVERITY, moneyJoin, problemTitle, shortDate } from './labels'
import { Card, Chip, ColumnHelpHeader, ColumnHelpPanel, FinanceQuery, HelpLabel, MoneyLines, NullValue, PageHead } from './ui'

const SEV_RANK: Record<string, number> = { critical: 0, warning: 1, preventable: 2 }

function nextStep(i: ProblemItem): string {
  if (i.kind === 'unknown_outcome') return 'zárolva, emberi döntésre vár'
  if (i.next_attempt_at) return `újrapróba: ${shortDate(i.next_attempt_at)}`
  if (i.inactivates_at) return `inaktiválás: ${shortDate(i.inactivates_at)}`
  return DASH
}

const code = (i: ProblemItem) => [i.stripe_code, i.decline_code].filter(Boolean).join(' · ') || DASH

export function ProblemsPage() {
  const fin = useFinance()
  const q = useApiQuery(() => adminFinance.problems(fin.projectId), [fin.projectId])
  const [kind, setKind] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [openKey, setOpenKey] = useState<string | null>(null)

  const columns: Column<ProblemItem>[] = useMemo(
    () => [
      {
        key: 'severity',
        label: <ColumnHelpHeader label="Súlyosság" helpKey="severity" openKey={openKey} setOpenKey={setOpenKey} />,
        sortValue: (i) => SEV_RANK[i.severity] ?? 9,
        format: (_v, i) => {
          const s = SEVERITY[i.severity] ?? { label: i.severity, tone: 'mute' as const }
          return <Chip tone={s.tone}>{s.label}</Chip>
        },
      },
      {
        key: 'kind',
        label: 'Probléma',
        sortValue: (i) => problemTitle(i),
        format: (_v, i) => {
          const flag = i.cover_flag ? COVER_FLAG[i.cover_flag] : undefined
          return (
            <span className="af-stack af-stack--tight">
              <strong>{problemTitle(i)}</strong>
              {i.cover_flag && (
                <span className="af-cover-flag" data-testid="cover-flag">
                  <Chip tone="crit">{flag?.label ?? i.cover_flag}</Chip>
                  {flag && <span className="af-sub af-wrap">{flag.text}</span>}
                </span>
              )}
              {i.unbooked_balance_cover && i.unbooked_balance_cover.length > 0 && (
                <span className="af-sub af-wrap">Könyveletlen egyenleg-levonás: {moneyJoin(i.unbooked_balance_cover)}</span>
              )}
            </span>
          )
        },
      },
      {
        key: 'email',
        label: 'Felhasználó',
        sortValue: (i) => i.email ?? '',
        format: (_v, i) => (i.email ? <Link to={userPage(i.email)}>{i.email}</Link> : '— (projekt szintű)'),
      },
      { key: 'project', label: 'Projekt', sortValue: (i) => i.project_name, format: (_v, i) => i.project_name },
      {
        key: 'amount',
        label: 'Összeg',
        numeric: true,
        sortValue: (i) => decimalSortKey(i.amount),
        format: (_v, i) =>
          i.amount ? (
            <span className="af-stack af-stack--tight">
              <span>{formatMoney(i.amount, i.currency)}</span>
              {i.covered_by_balance && <span className="af-sub af-small">ebből egyenlegből: {formatMoney(i.covered_by_balance, i.currency)}</span>}
            </span>
          ) : (
            DASH
          ),
      },
      { key: 'since', label: 'Mióta', sortValue: (i) => i.since, format: (_v, i) => shortDate(i.since) },
      {
        key: 'attempt',
        label: <ColumnHelpHeader label="Próbálkozás" helpKey="attempt" openKey={openKey} setOpenKey={setOpenKey} />,
        numeric: true,
        sortValue: (i) => i.attempt ?? null,
        format: (_v, i) => (i.attempt ? `${i.attempt} / ${i.max_attempts ?? '?'}` : DASH),
      },
      { key: 'next', label: 'Következő lépés', sortable: false, format: (_v, i) => nextStep(i) },
      { key: 'code', label: 'Stripe-kód / ok', sortValue: (i) => code(i), format: (_v, i) => <span className="af-num af-small">{code(i)}</span> },
      { key: 'do', label: 'Teendő', sortable: false, format: (_v, i) => <ProblemActions item={i} /> },
    ],
    [openKey],
  )

  return (
    <>
      <PageHead title="Figyelmet igényel" sub="Minden nyitott tétel egy listában, súlyosság szerint">
        <label className="af-search">
          Keresés
          <input type="search" placeholder="e-mail, projekt, Stripe-kód…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </PageHead>
      <FinanceQuery q={q}>
        {(data) => {
          const bySev = (s: string) => data.items.filter((i) => i.severity === s).length
          const countSum = Object.values(data.counts).some((n) => n === null || n === undefined)
            ? null
            : Object.values(data.counts).reduce<number>((a, n) => a + (n ?? 0), 0)
          const needle = search.trim().toLowerCase()
          const rows = data.items.filter(
            (i) =>
              (kind === 'all' || i.kind === kind) &&
              (!needle || [i.email, i.project_name, i.stripe_code, i.decline_code, problemTitle(i)].some((v) => v?.toLowerCase().includes(needle))),
          )
          return (
            <>
              <section className="af-kpis af-kpis--4">
                <Card>
                  <HelpLabel helpKey="atRisk" className="af-lbl">
                    Rendezésre váró összeg
                  </HelpLabel>
                  <div className="af-kv af-num af-kv--sm">
                    <MoneyLines list={data.at_risk} />
                  </div>
                  {data.at_risk_covered_by_balance && (
                    <div className="af-sub" data-testid="at-risk-covered">
                      ebből egyenlegből fedezhető: <span className="af-num">{moneyJoin(data.at_risk_covered_by_balance)}</span>
                    </div>
                  )}
                </Card>
                <Card>
                  <HelpLabel helpKey="severity" className="af-lbl">
                    Kritikus
                  </HelpLabel>
                  <div className="af-kv af-num af-kv--crit">{formatCount(bySev('critical'))}</div>
                  <div className="af-sub">ismeretlen kimenetű terhelés – az újrapróbálás zárolva</div>
                </Card>
                <Card>
                  <div className="af-lbl">Figyelem</div>
                  <div className="af-kv af-num af-kv--warn">{formatCount(bySev('warning'))}</div>
                  <div className="af-sub">elutasított kártya, tartozás, negatív kredit, zárás</div>
                </Card>
                <Card>
                  <div className="af-lbl">Megelőzhető</div>
                  <div className="af-kv af-num af-kv--info">{formatCount(bySev('preventable'))}</div>
                  <div className="af-sub">lejáró kártya a következő megújítás előtt, ismételt hiba</div>
                </Card>
              </section>
              <div role="group" aria-label="Szűrés probléma szerint" className="af-filters">
                {[['all', 'Mind'] as const, ...KIND_ORDER.map((k) => [k, KIND_LABEL[k]] as const)].map(([k, label]) => {
                  const n = k === 'all' ? countSum : data.counts[k as keyof typeof data.counts] ?? null
                  return (
                    <button key={k} type="button" className={kind === k ? 'af-filter on' : 'af-filter'} aria-pressed={kind === k} onClick={() => setKind(k)}>
                      {label} <span className="af-num af-filter__n">{n === null ? <NullValue /> : formatCount(n)}</span>
                    </button>
                  )
                })}
              </div>
              <Card>
                <ColumnHelpPanel openKey={openKey} />
                {rows.length === 0 ? (
                  <p className="portal-text-muted" data-testid="problems-empty">
                    Nincs a szűrésnek megfelelő tétel.
                  </p>
                ) : (
                  <div className="portal-table-scroll">
                    <Table columns={columns} rows={rows} rowKey={(i, n) => `${i.kind}-${i.ref?.id ?? n}-${i.email ?? ''}`} initialSort={{ key: 'severity', dir: 'asc' }} />
                  </div>
                )}
                <p className="af-sub af-note">
                  Minden kézi művelet megerősítést kér, és bekerül az admin-műveleti naplóba (ki, mikor, mit, melyik tételen). Pénzügyi rekord nem törlődik – a lezárás új állapotot ír.
                </p>
              </Card>
            </>
          )
        }}
      </FinanceQuery>
    </>
  )
}
